import React, { useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { LogOut, LayoutDashboard, PlusCircle, KeyRound, ClipboardList, FileText } from "lucide-react";
import { useNavigate, useLocation } from "react-router-dom";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import apiClient from "@/api/client";

const RegistrationLayout = ({ children }) => {
  const { user, logout, hasCrmAccess } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [showPwdModal, setShowPwdModal] = useState(false);
  const [pwdForm, setPwdForm] = useState({ current: "", new: "", confirm: "" });
  const [pwdError, setPwdError] = useState("");
  const [pwdSuccess, setPwdSuccess] = useState("");
  const [pwdLoading, setPwdLoading] = useState(false);
  
  const isOnCashEntry = location.pathname === "/cash-entry";
  const isOnInscription = location.pathname === "/inscription";
  const isOnPrestations = location.pathname === "/prestations";
  const isOnMutuelle = location.pathname === "/mutuelle";

  const handleLogout = () => {
    logout();
    navigate("/login");
  };

  const handlePasswordChange = async () => {
    setPwdError("");
    setPwdSuccess("");
    if (pwdForm.new.length < 6) {
      setPwdError("Le nouveau mot de passe doit contenir au moins 6 caractères");
      return;
    }
    if (pwdForm.new !== pwdForm.confirm) {
      setPwdError("Les mots de passe ne correspondent pas");
      return;
    }
    setPwdLoading(true);
    try {
      await apiClient.post("/auth/change-password", {
        current_password: pwdForm.current,
        new_password: pwdForm.new,
      });
      setPwdSuccess("Mot de passe modifié avec succès !");
      setPwdForm({ current: "", new: "", confirm: "" });
      setTimeout(() => { setShowPwdModal(false); setPwdSuccess(""); }, 1500);
    } catch (err) {
      setPwdError(err.response?.data?.detail || "Erreur lors du changement");
    } finally {
      setPwdLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#f4f7fa]">
      <div className="bg-[#1a2744] text-white px-6 py-3 flex items-center justify-between shadow-md">
        <div className="flex items-center gap-3">
          <span className="text-[#e8d44d] font-bold text-lg">B.health</span>
          <span className="text-[#8fa4b5] text-sm">| {isOnCashEntry ? "Paiement hors abonnement" : isOnPrestations ? "Mes Prestations" : "Formulaire d'inscription"}</span>
        </div>
        <div className="flex items-center gap-4">
          {!isOnInscription && (
            <button onClick={() => navigate("/inscription")} className="flex items-center gap-1.5 px-3 py-1.5 text-sm bg-[#3899ec] text-white rounded-md hover:bg-[#2a7bc9] transition-colors font-medium" data-testid="goto-inscription-btn">
              Inscription
            </button>
          )}
          {!isOnCashEntry && (
            <button onClick={() => navigate("/cash-entry")} className="flex items-center gap-1.5 px-3 py-1.5 text-sm bg-[#22c55e] text-white rounded-md hover:bg-[#16a34a] transition-colors font-medium" data-testid="goto-cash-entry-btn">
              <PlusCircle size={16} />
              Paiement
            </button>
          )}
          {!isOnPrestations && (
            <button onClick={() => navigate("/prestations")} className="flex items-center gap-1.5 px-3 py-1.5 text-sm bg-[#f59e0b] text-white rounded-md hover:bg-[#d97706] transition-colors font-medium" data-testid="goto-prestations-btn">
              <ClipboardList size={16} />
              Prestations
            </button>
          )}
          {!isOnMutuelle && (
            <button onClick={() => navigate("/mutuelle")} className="flex items-center gap-1.5 px-3 py-1.5 text-sm bg-[#8b5cf6] text-white rounded-md hover:bg-[#7c3aed] transition-colors font-medium" data-testid="goto-mutuelle-btn">
              <FileText size={16} />
              Mutuelle
            </button>
          )}
          {hasCrmAccess && (
            <button onClick={() => navigate("/dashboard")} className="flex items-center gap-1.5 text-sm text-[#c1c9d0] hover:text-[#e8d44d] transition-colors" data-testid="goto-crm-btn">
              <LayoutDashboard size={16} />
              Accéder au CRM
            </button>
          )}
          <button onClick={() => setShowPwdModal(true)} className="flex items-center gap-1.5 text-sm text-[#c1c9d0] hover:text-[#e8d44d] transition-colors" data-testid="change-password-btn">
            <KeyRound size={16} />
          </button>
          <span className="text-sm text-[#8fa4b5]">{user?.name || user?.email}</span>
          <button onClick={handleLogout} className="flex items-center gap-1.5 text-sm text-[#c1c9d0] hover:text-red-400 transition-colors" data-testid="logout-btn">
            <LogOut size={16} />
            Déconnexion
          </button>
        </div>
      </div>
      <div className="py-4">
        {children}
      </div>

      <Dialog open={showPwdModal} onOpenChange={setShowPwdModal}>
        <DialogContent className="sm:max-w-[400px]">
          <DialogHeader>
            <DialogTitle>Changer le mot de passe</DialogTitle>
          </DialogHeader>
          <div className="flex flex-col gap-3 py-2">
            <Input type="password" placeholder="Mot de passe actuel" value={pwdForm.current} onChange={(e) => setPwdForm({...pwdForm, current: e.target.value})} data-testid="current-password-input" />
            <Input type="password" placeholder="Nouveau mot de passe (min. 6 car.)" value={pwdForm.new} onChange={(e) => setPwdForm({...pwdForm, new: e.target.value})} data-testid="new-password-input" />
            <Input type="password" placeholder="Confirmer le nouveau mot de passe" value={pwdForm.confirm} onChange={(e) => setPwdForm({...pwdForm, confirm: e.target.value})} data-testid="confirm-password-input" />
            {pwdError && <p className="text-sm text-red-500">{pwdError}</p>}
            {pwdSuccess && <p className="text-sm text-green-600">{pwdSuccess}</p>}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowPwdModal(false)}>Annuler</Button>
            <Button onClick={handlePasswordChange} disabled={pwdLoading} className="bg-[#1a2744] hover:bg-[#243459] text-[#e8d44d]" data-testid="submit-password-change">
              {pwdLoading ? "..." : "Modifier"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default RegistrationLayout;
